import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = ["super_admin", "content_admin", "programme_editor", "communications_editor", "data_viewer"];

export default function AdminUsers() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [newUser, setNewUser] = useState({ name: "", email: "", role: "data_viewer" });
    const [lastTemp, setLastTemp] = useState(null);

    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/users"); setUsers(data.users); }
        catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function create(e) {
        e.preventDefault();
        try {
            const { data } = await api.post("/admin/users", newUser);
            toast.success("User created");
            setLastTemp({ email: newUser.email, password: data.temporary_password });
            setNewUser({ name: "", email: "", role: "data_viewer" });
            setShowCreate(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }
    async function updateRole(uid, role) {
        try { await api.patch(`/admin/users/${uid}`, { role }); toast.success("Role updated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }
    async function toggleActive(uid, active) {
        try { await api.patch(`/admin/users/${uid}`, { is_active: active }); toast.success("Updated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }
    async function resetPassword(uid, email) {
        if (!confirm(`Reset password for ${email}?`)) return;
        try {
            const { data } = await api.post(`/admin/users/${uid}/reset-password`);
            setLastTemp({ email, password: data.temporary_password });
            toast.success("Password reset — share the temporary password securely");
        } catch (e) { toast.error(formatApiError(e)); }
    }

    const canManage = user?.role === "super_admin";

    return (
        <div data-testid="admin-users">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="gsf-eyebrow">Access</div>
                    <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Administrators</h1>
                </div>
                {canManage && (
                    <button onClick={() => setShowCreate((v) => !v)} className="gsf-btn-primary text-sm" data-testid="add-user-btn"><UserPlus size={16} /> Add user</button>
                )}
            </div>

            {lastTemp && (
                <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="temp-password-notice">
                    <div className="text-xs uppercase tracking-widest text-amber-700">Temporary password (share securely)</div>
                    <div className="mt-2 font-mono text-sm break-all"><strong>{lastTemp.email}</strong> — <code>{lastTemp.password}</code></div>
                    <p className="mt-2 text-xs text-amber-700">The user must change this password on first login.</p>
                    <button onClick={() => setLastTemp(null)} className="mt-2 text-xs underline">Dismiss</button>
                </div>
            )}

            {showCreate && (
                <form onSubmit={create} className="mt-6 gsf-card p-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="create-user-form">
                    <input required placeholder="Full name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-name" className="rounded-lg border border-gray-200 px-3 py-2" />
                    <input required type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-email" className="rounded-lg border border-gray-200 px-3 py-2" />
                    <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="new-role" className="rounded-lg border border-gray-200 px-3 py-2">
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button type="submit" className="gsf-btn-primary text-sm justify-center" data-testid="new-submit">Create</button>
                </form>
            )}

            <div className="mt-6 gsf-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#f8faf7] text-left">
                        <tr><th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Role</th><th className="p-3">MFA</th><th className="p-3">Active</th><th className="p-3">Actions</th></tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.id}`}>
                                <td className="p-3">{u.email}</td>
                                <td className="p-3">{u.name}</td>
                                <td className="p-3">
                                    {canManage ? (
                                        <select value={u.role} onChange={(e) => updateRole(u.id, e.target.value)} data-testid={`role-${u.id}`} className="rounded border border-gray-200 px-2 py-1">
                                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    ) : <span>{u.role}</span>}
                                </td>
                                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${u.mfa_enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{u.mfa_enabled ? "Enabled" : "Not enrolled"}</span></td>
                                <td className="p-3">
                                    {canManage ? (
                                        <input type="checkbox" checked={u.is_active} onChange={(e) => toggleActive(u.id, e.target.checked)} data-testid={`active-${u.id}`} />
                                    ) : <span>{u.is_active ? "Yes" : "No"}</span>}
                                </td>
                                <td className="p-3">
                                    {canManage && (
                                        <button onClick={() => resetPassword(u.id, u.email)} className="text-[color:var(--gsf-green-700)] hover:bg-[color:var(--gsf-soft-green)] rounded-lg p-1.5" data-testid={`reset-${u.id}`}><KeyRound size={16} /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
